import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ES_ECOLAB } from '@/config/authConfig';

// Acceso dinámico para evitar dependencia circular store ↔ axiosConfig
const getStore = () => require('@/store').store;
const getAuthActions = () => require('@/store/authSlice');

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5246/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: inyectar JWT ─────────────────────────────────────
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // El store se actualiza cuando el thunk de login se resuelve, pero
    // loginAsync llama a /usuario/me ANTES de eso. En ese momento el token
    // ya está en localStorage pero todavía no en Redux, así que hay que
    // mirar los dos: primero el store, y si está vacío, localStorage.
    const token = getStore().getState().auth.token || localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Empresa activa (la persona puede estar en varias empresas)
    let empresa: string | null = null;
    try { empresa = localStorage.getItem('pos_empresa'); } catch { /* sin almacenamiento */ }
    if (empresa && config.headers) config.headers['X-Empresa-Id'] = empresa;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response: manejar 401 ─────────────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{ resolve: (t: string) => void; reject: (e: Error) => void }> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !original._retry) {
      // Si es el propio endpoint de login o la app del domiciliario, no redirigir
      if (original.url?.includes('/usuario/login') ||
          original.url?.includes('/ecolab/auth/') ||
          original.url?.includes('/domiciliario-app')) {
        return Promise.reject(error);
      }

      // No redirigir si estamos en la app del domiciliario
      if (window.location.pathname.startsWith('/domiciliario')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            original.headers!.Authorization = `Bearer ${token}`;
            return api(original);
          })
          .catch(Promise.reject);
      }

      original._retry = true;
      isRefreshing = true;

      try {
        let nuevoToken: string;

        if (ES_ECOLAB) {
          // El token lo emite Ecolab, así que la renovación va contra
          // Ecolab. /usuario/me del POS ya no devuelve token.
          // Import dinámico: ecolabAuthService no depende de axiosConfig,
          // pero se carga aquí para no crear ciclos en el bundle.
          const { default: ecolabAuthService } = await import('./ecolabAuthService');
          const renovado = await ecolabAuthService.refresh();
          if (!renovado) throw new Error('No se pudo renovar la sesión');
          nuevoToken = renovado;
        } else {
          // Modo Local: /usuario/me devuelve un token fresco.
          const token = getStore().getState().auth.token;
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5246/api'}/usuario/me`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          nuevoToken = data.token;
        }

        getStore().dispatch(getAuthActions().refreshTokenAction(nuevoToken));
        processQueue(null, nuevoToken);
        original.headers!.Authorization = `Bearer ${nuevoToken}`;
        return api(original);
      } catch {
        processQueue(new Error('Session expired'), null);
        getStore().dispatch(getAuthActions().logout());
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

export const apiGet  = <T>(url: string, params?: Record<string, unknown>) => api.get<T>(url, { params });
export const apiPost = <T>(url: string, data?: unknown) => api.post<T>(url, data);
export const apiPut  = <T>(url: string, data?: unknown) => api.put<T>(url, data);
export const apiPatch= <T>(url: string, data?: unknown) => api.patch<T>(url, data);
export const apiDelete = <T>(url: string) => api.delete<T>(url);
